export const LOCALES = ["en", "de", "fr", "it", "pl", "uk", "ar", "hi", "uz", "th", "ur"] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "EN",
  uk: "UA",
  ar: "AR",
  hi: "HI",
  uz: "UZ",
  pl: "PL",
  de: "DE",
  fr: "FR",
  it: "IT",
  th: "TH",
  ur: "UR",
};

export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  uk: "Українська",
  ar: "العربية",
  hi: "हिन्दी",
  uz: "Oʻzbekcha",
  pl: "Polski",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
  th: "ไทย",
  ur: "اردو",
};

/** Short native hint shown under each language: the market the version serves. */
export const LOCALE_HINTS: Record<Locale, string> = {
  en: "Global · manufacturing worldwide",
  uk: "Україна · виробництво українською",
  ar: "الشرق الأوسط · التصنيع بالعربية",
  hi: "भारत · हिन्दी में विनिर्माण",
  uz: "Oʻzbekiston · ishlab chiqarish",
  pl: "Polska · produkcja po polsku",
  de: "DACH · Fertigung auf Deutsch",
  fr: "France · industrie en français",
  it: "Italia · produzione in italiano",
  th: "ประเทศไทย · การผลิตภาษาไทย",
  ur: "پاکستان · اردو میں مینوفیکچرنگ",
};

/** BCP-47 codes used for <html lang> and hreflang. */
export const LOCALE_HREFLANG: Record<Locale, string> = {
  en: "en",
  uk: "uk",
  ar: "ar",
  hi: "hi",
  uz: "uz",
  pl: "pl",
  de: "de",
  fr: "fr",
  it: "it",
  th: "th",
  ur: "ur",
};

export const RTL_LOCALES: Locale[] = ["ar", "ur"];

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

export function normalizeLocale(value: unknown): Locale {
  return isLocale(value) ? value : DEFAULT_LOCALE;
}

export function dirFor(locale: Locale): "ltr" | "rtl" {
  return RTL_LOCALES.includes(locale) ? "rtl" : "ltr";
}

/** Build a path for a locale. English lives at the root, others are prefixed. */
export function localePath(locale: Locale, path: string): string {
  const clean = path === "/" ? "" : path.startsWith("/") ? path : `/${path}`;
  if (locale === DEFAULT_LOCALE) return clean === "" ? "/" : clean;
  return `/${locale}${clean}`;
}

export const SITE_URL = "https://factory.online";

export function alternateLinks(path: string) {
  return [
    ...LOCALES.map((locale) => ({
      rel: "alternate",
      hrefLang: LOCALE_HREFLANG[locale],
      href: `${SITE_URL}${localePath(locale, path)}`,
    })),
    { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}${localePath("en", path)}` },
  ];
}
